import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Button, Table, Card, CardBody, Row, Col, Badge } from "reactstrap";
import i18n from "../../../../i18n";
import { bulkTaskStatusUpdate, bulkSummaryGet } from "../../../../store/bulk-schedule/actions";
import { postBulkScheduleProcess } from "../../../../helpers/api_rombo_helper";

const selectBulk = createSelector(
  (state) => state.BulkSchedule,
  (bs) => ({ summary: bs.summary, taskId: bs.taskId })
);

const selectLogin = createSelector(
  (state) => state.Login,
  (l) => ({ token: l.token })
);

const StepSummary = () => {
  const dispatch = useDispatch();
  const { summary, taskId } = useSelector(selectBulk);
  const { token } = useSelector(selectLogin);
  const [expandedServices, setExpandedServices] = useState({});
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!summary && taskId) {
      dispatch(bulkSummaryGet(taskId, "json"));
    }
  }, [summary, taskId, dispatch]);

  if (!summary || !summary.details) {
    return (
      <div className="text-center py-5">
        <p>{i18n.t("LoadingSummary")}</p>
      </div>
    );
  }

  const normalizeKeys = (items) =>
    items.map((item) =>
      Object.fromEntries(
        Object.entries(item).map(([key, value]) => [key.trim(), value])
      )
    );

  const normalizedDetails = normalizeKeys(summary.details);

  const { document_id, status } = summary;

  const COLUMN_CONFIG = [
    { key: "Nombre del usuario", label: "Nombre del usuario" },
    { key: "Calle", label: "Calle" },
    { key: "Colonia", label: "Colonia" },
    { key: "Latitud y Longitud", label: "Latitud y Longitud" },
    { key: "Referencias", label: "Referencias" },
    { key: "Teléfonos", label: "Teléfono" },
    { key: "Fecha", label: "Fecha" },
    { key: "Hora", label: "Hora" }
  ];

  const groupByPlates = (items) => {
    const grouped = {};
    items.forEach((item) => {
      const plate = item["Placas"] || "SIN PLACA";
      if (!grouped[plate]) grouped[plate] = [];
      grouped[plate].push(item);
    });
    return grouped;
  };

  const groupedServices = groupByPlates(normalizedDetails);
  const serviceGroups = Object.entries(groupedServices);
  const servicesCount = serviceGroups.length;

  const toggleService = (plate) => {
    setExpandedServices((prev) => ({
      ...prev,
      [plate]: !prev[plate]
    }));
  };

  const handleConfirm = async () => {
    await postBulkScheduleProcess(token, document_id);
    dispatch(bulkTaskStatusUpdate("processing", []));
  };

  const handleCancel = () => {
    dispatch(bulkTaskStatusUpdate(null, []));
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "success":
        return i18n.t("Validated");
      case "error":
        return i18n.t("Error");
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "success";
      case "error":
        return "danger";
      default:
        return "secondary";
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(document_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div>
      <div className="summary-header mb-4">
        <Row>
          <Col md={6}>
            <h5 className="mb-3">{i18n.t("ScheduleSummary")}</h5>
          </Col>
        </Row>
        <Card className="mb-4 bg-light">
          <CardBody>
            <Row>
              <Col md={4}>
                <div className="d-flex align-items-center justify-content-between">
                  <p className="mb-0 text-muted">{i18n.t("DocumentId")}</p>
                  <button
                    type="button"
                    className="btn p-0"
                    onClick={handleCopy}
                    title={copied ? i18n.t("Copied") : i18n.t("Copy")}
                    style={{ lineHeight: 1 }}
                  >
                    <i className={`mdi ${copied ? "mdi-check" : "mdi-content-copy"}`} />
                  </button>
                </div>
                <strong>{document_id}</strong>
              </Col>
              <Col md={2}>
                <p className="mb-0 text-muted">{i18n.t("Status")}</p>
                <Badge color={getStatusColor(status)}>
                  {getStatusLabel(status)}
                </Badge>
              </Col>
              <Col md={3}>
                <p className="mb-0 text-muted">{i18n.t("RecordsFound")}</p>
                <strong>{normalizedDetails.length}</strong>
              </Col>
              <Col md={3}>
                <p className="mb-0 text-muted">{i18n.t("ServicesToCreate")}</p>
                <strong>{servicesCount}</strong>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </div>
      <div className="services-scroll-container">
        {serviceGroups.map(([plate, services]) => {
          const isExpanded = expandedServices[plate];
          const passengerCount = services.length;

          return (
            <Card key={plate} className="mb-4 service-card">
              <CardBody>
                <div
                  className="service-header d-flex justify-content-between align-items-center"
                  onClick={() => toggleService(plate)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <i className={`mdi mdi-${isExpanded ? "chevron-down" : "chevron-right"} mdi-24px`} />
                    <div>
                      <h6 className="mb-0">
                        {i18n.t("ServiceFor")} <strong>{plate}</strong>
                      </h6>
                      <small className="text-muted">
                        <i className="mdi mdi-account-multiple-outline me-1" />
                        {passengerCount}{" "}
                        {passengerCount === 1
                          ? i18n.t("Passenger")
                          : i18n.t("Passengers")}
                      </small>
                    </div>
                  </div>
                  <i
                    className="mdi mdi-car mdi-24px"
                    style={{ color: isExpanded ? "#FF5400" : "#CDD5E0", transition: "color 0.25s ease" }}
                  />
                </div>
                {isExpanded && (
                  <div className="service-table-container mt-3">
                    <div className="table-responsive">
                      <Table hover striped size="sm" className="mb-0 align-middle">
                        <thead>
                          <tr>
                            {COLUMN_CONFIG.map((col, idx) => (
                              <th key={idx}>{col.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {services.map((item, idx) => (
                            <tr key={idx}>
                              {COLUMN_CONFIG.map((col, colIdx) => (
                                <td key={colIdx}>
                                  {item[col.key] ?? "-"}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          );
        })}
      </div>
      <div className="summary-actions mt-4 d-flex justify-content-end gap-3">
        <Button color="btn btn-go-back" onClick={handleCancel} className="px-4">
          {i18n.t("Cancel")}
        </Button>
        <Button color="btn btn-request" onClick={handleConfirm} className="px-4">
          {i18n.t("ConfirmUpload")}
        </Button>
      </div>
    </div>
  );
};

export default StepSummary;
